// The console's router guard — spec 15 §3.1.6: "the SPA admin area mirrors this
// — router guard requires /v1/platform/me 200 before rendering anything; 401 →
// login screen".
//
// THE PROBE IS THE AUTHORITY. The session is an opaque `__Host-` cookie the
// browser holds and this code cannot read, so there is nothing to inspect
// locally and nothing to cache a "logged in" flag from — asking the server is
// the ONLY way to know, and it is what this component does before rendering a
// single child. That is also why sign-out needs no client-side token clearing:
// the cookie stops working and the next probe says so.
//
// THE PHASE RULE, on top of the 200 (§3.1.3, acceptance M15-S03: "they are
// forced into the enrollment screen and cannot reach the tenant list until a
// valid code is confirmed"):
//
//   requires="confirmed"  /console and every future console screen. A staff
//                         member who has not finished TOTP is bounced to
//                         /enroll — their restricted session would 403 on
//                         everything here anyway, and a wall of 403s is a worse
//                         answer than the screen that fixes it.
//   requires="pending"    /enroll itself. Reachable ONLY while enrolment is
//                         unfinished; once confirmed it is a dead end that
//                         would 409 STAFF_NOT_TOTP_PENDING, so a confirmed
//                         staff member goes to /console.
//
// A redirect is an EFFECT, never a render-time side effect: navigating during
// render is what makes TanStack warn and what makes a redirect loop invisible.
//
// AND IT NEVER ACTS ON AN ANSWER IT KNOWS IS BEING REPLACED. `wrongPhase` is
// computed from a CACHED `/me`; while a fetch for that key is in flight the
// cached value is, by definition, the one the app has already decided it does
// not trust. Redirecting off it produced a visible defect — a confirmed staff
// member bounced from `/console` back to `/enroll`, which re-fired an enrolment
// the API refuses 409 — so the effect waits for the fetch to settle and the
// screen holds the checking state meanwhile. It waits for SETTLED, not for
// success: a failed refetch still ends the fetch, so this can never hang on a
// network that never answers.

import { useEffect, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Banner } from "../../../components/toast";
import { AdminAuthCard } from "../admin-shell";
import { PlatformError, type PlatformMe } from "../lib/platform-api";
import { usePlatformSession } from "../hooks/use-platform-session";

export interface RequirePlatformSessionProps {
  requires: "confirmed" | "pending";
  children: (me: PlatformMe) => ReactNode;
  /** Wraps the checking and error cards. The console layout passes the
   * sign-in frame, because its own shell must not paint until the probe says
   * this person may see it (D-626). Without it the cards render bare. */
  frame?: (node: ReactNode) => ReactNode;
}

export function RequirePlatformSession({
  requires,
  children,
  frame = (node) => node,
}: RequirePlatformSessionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = usePlatformSession();

  const { data: me, error } = session;
  // 401 is the ANSWER "you have no session", not a failure to reach the server:
  // no session cookie, an expired one, or one revoked by sign-out elsewhere.
  const unauthenticated = error instanceof PlatformError && error.status === 401;
  const confirmed = me?.totp_confirmed === true;
  const wrongPhase = me !== undefined && (requires === "confirmed" ? !confirmed : confirmed);
  const destination = requires === "confirmed" ? "/enroll" : "/console";
  // A fetch in flight means the cached answer is already under replacement.
  const settled = !session.isFetching;

  useEffect(() => {
    if (unauthenticated) {
      void navigate({ to: "/login", replace: true });
      return;
    }
    if (wrongPhase && settled) {
      void navigate({ to: destination, replace: true });
    }
  }, [unauthenticated, wrongPhase, settled, destination, navigate]);

  // ---- loading -------------------------------------------------------------
  // Also covers the two redirect frames above: rendering the destination's
  // chrome for one frame before navigating is the layout shift this avoids.
  if (session.isPending || unauthenticated || wrongPhase) {
    return frame(
      <AdminAuthCard title={t("admin.guard.checking")} data-testid="admin-guard-loading">
        <div className="admin-state">
          <p>{t("admin.guard.checking")}</p>
        </div>
      </AdminAuthCard>,
    );
  }

  // ---- error (not a 401 — a real one: network, 5xx) -------------------------
  if (me === undefined) {
    return frame(
      <AdminAuthCard title={t("admin.guard.errorTitle")} data-testid="admin-guard-error">
        <Banner kind="bad">{t("admin.guard.errorBody")}</Banner>
        <div className="admin-actions">
          <Button
            variant="primary"
            busy={session.isFetching}
            onClick={() => {
              void session.refetch();
            }}
            data-testid="admin-guard-retry"
          >
            {t("admin.guard.retry")}
          </Button>
        </div>
      </AdminAuthCard>,
    );
  }

  return <>{children(me)}</>;
}
